import { FormGroup, FormControl } from '../../../components/Form';
import { IGratuityPanel } from '../../redux/GratuityPanel/state';
import { IUIDropDown } from '../../../redux/initialStates/ui/dropDownState';
import { IUpdateLocationParam } from '../LocationsEditPage';
import { LOCATION_SETTINGS_ACTIONS } from '../../redux/LocationSettingsActions';
import { selectGratuityPanel } from '../../redux/GratuityPanel/selectors';
import { useAppSelector } from '../../../redux/store';
import React from 'react';
import TextField from '../../../components/TextField/TextField';
import Toggle from '../../../components/Toggle/Toggle';
import {
  TipCalculationRow,
  TipValueInputs,
  CustomTipSelector,
  TipToggleMessage,
  ForcedTipSection,
  DEFAULT_TIP_PLACEHOLDERS,
  DEFAULT_SERVICE_CHARGE_TIP_PLACEHOLDERS,
  TIP_MESSAGE_MAX_LENGTH
} from '../../../components/TipConfiguration';
import styles from './LocationPanelGratuity.module.scss';

interface Props {
  updateLocationParam: IUpdateLocationParam;
  isProcessing: boolean;
  dropDown: IUIDropDown;
}



const LocationPanelGratuity = (props: Props) => {
  const { updateLocationParam, isProcessing } = props;
  const gratuityPanel = useAppSelector<IGratuityPanel>(selectGratuityPanel);

  return (
    <FormGroup groupName='Gratuities'>
      <FormControl label='Allow Tips'>
        <Toggle
          checked={gratuityPanel.allowTips}
          onChange={(checked: boolean) => {
            updateLocationParam(checked, LOCATION_SETTINGS_ACTIONS.TOGGLE_ALLOW_TIPS);
          }}
          loading={isProcessing}
        />
      </FormControl>

      <FormControl label='Default Tip Values'>
        <TipValueInputs
          values={gratuityPanel.defaultTips}
          onChange={(index: number, value: number) => {
            const newval = {
              tip_value: value,
              tip_index: index,
            };
            updateLocationParam(newval, LOCATION_SETTINGS_ACTIONS.UPDATE_TIP_VALUE);
          }}
          placeholders={DEFAULT_TIP_PLACEHOLDERS}
          loading={isProcessing}
          keyPrefix="default-tip"
        />
      </FormControl>

      <FormControl
        label='Default Tip Values for Service Charges'
        labelTooltip='These defaults will be displayed to guests if a service charge is present on the check. Otherwise, the regular Default Tip Values will be displayed.'
      >
        <TipValueInputs
          values={gratuityPanel.defaultTipsForServiceCharges}
          onChange={(index: number, value: number) => {
            const newval = {
              service_tip_value: value,
              service_tip_index: index,
            };
            updateLocationParam(newval, LOCATION_SETTINGS_ACTIONS.UPDATE_SERVICE_CHARGE_TIP_VALUE);
          }}
          placeholders={DEFAULT_SERVICE_CHARGE_TIP_PLACEHOLDERS}
          loading={isProcessing}
          keyPrefix="service-tip"
        />
      </FormControl>

      <FormControl label='Default "Custom Tip" Selection'>
        <div className={styles.defaultCustomTip}>
          <CustomTipSelector
            value={gratuityPanel.defaultCustomTipType}
            onChange={(value) => {
              updateLocationParam(value, LOCATION_SETTINGS_ACTIONS.UPDATE_DEFAULT_CUSTOM_TIP);
            }}
            loading={isProcessing}
          />
        </div>
      </FormControl>

			<FormControl label='Tip Calculation'>
				<div className='control--stacked'>
					<TextField value='Calculate tips based on the total amount:' />
				</div>
				<div className='control--stacked'>
					<TipCalculationRow
						value={gratuityPanel.preDiscountTip}
						onChange={(value: boolean) => {
							updateLocationParam(value, LOCATION_SETTINGS_ACTIONS.UPDATE_PRE_DISCOUNT_TIP);
						}}
						label="Check discounts"
						loading={isProcessing}
					/>
				</div>
				<div className='control--stacked'>
					<TipCalculationRow
						value={gratuityPanel.preItemDiscountTip}
						onChange={(value: boolean) => {
							updateLocationParam(value, LOCATION_SETTINGS_ACTIONS.UPDATE_PRE_ITEM_DISCOUNT_TIP);
						}}
						label="Item discounts"
						loading={isProcessing}
					/>
				</div>
				<div className='control--stacked'>
					<TipCalculationRow
						value={gratuityPanel.preTaxTip}
						onChange={(value: boolean) => {
							updateLocationParam(value, LOCATION_SETTINGS_ACTIONS.UPDATE_PRE_TAX_TIP);
						}}
						label="Tax"
						loading={isProcessing}
					/>
				</div>
				<div className='control--stacked'>
					<TipCalculationRow
						value={gratuityPanel.preNonReadyPaymentsTip}
						onChange={(value: boolean) => {
							updateLocationParam(value, LOCATION_SETTINGS_ACTIONS.UPDATE_PRE_NON_READY_PAYMENTS_TIP);
						}}
						label="Non-Ready payments"
						loading={isProcessing}
					/>
				</div>
			</FormControl>

      <FormControl label='Display Custom Message'>
        <TipToggleMessage
          checked={gratuityPanel.showBottomText}
          onToggleChange={(checked: boolean) => {
            updateLocationParam(checked, LOCATION_SETTINGS_ACTIONS.TOGGLE_SHOW_BOTTOM);
          }}
          message={gratuityPanel.bottomText ?? ''}
          onMessageChange={(message: string) => {
            updateLocationParam(message, LOCATION_SETTINGS_ACTIONS.UPDATE_BOTTOM_TEXT);
          }}
          loading={isProcessing}
          maxLength={TIP_MESSAGE_MAX_LENGTH}
        />
      </FormControl>

      <FormControl
        label='Forced Tip'
        labelTooltip='When enabled, guests will see a message about automatic gratuity instead of tip options'
      >
        <ForcedTipSection
          enabled={gratuityPanel.forcedTipEnabled ?? false}
          onEnabledChange={(enabled: boolean) => {
            updateLocationParam(enabled, LOCATION_SETTINGS_ACTIONS.TOGGLE_FORCED_TIP);
          }}
          percentage={gratuityPanel.forcedTipPercentage ?? 0}
          onPercentageChange={(percentage: number) => {
            updateLocationParam(percentage, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_TIP_PERCENTAGE);
          }}
          loading={isProcessing}
          placeholder="0"
        />
      </FormControl>

    </FormGroup>
  );
};

export default LocationPanelGratuity;
